/**
 * Activity Type Constants
 *
 * Centralized constants for all activity types used throughout the application.
 * Use these constants instead of hardcoded strings to prevent typos and improve maintainability.
 *
 * @example
 * import { ACTIVITY_TYPES } from '@/constants/ActivityTypes.js';
 *
 * if (activity.activity_type === ACTIVITY_TYPES.DISCUSSION_THREAD) {
 *     // Handle discussion thread logic
 * }
 */

export const ACTIVITY_TYPES = {
    ACKNOWLEDGE_INFORMATION: 'acknowledge-information',
    DISCUSSION_THREAD: 'discussion-thread',
    FILE_SUBMISSION: 'file-submission',
    PRESENTATION: 'presentation',
    QUIZ: 'quiz',
    SLIDE_DECK: 'slide-deck',
    SURVEY: 'survey',
    VIDEO: 'video',
    PODCAST: 'podcast',
    VIEW_INFORMATION: 'view-information',
    LTI_RESOURCE_LINK: 'lti-resource-link',
    LTI_TOOL: 'lti-tool',
    EBOOK: 'e-book',
    ELEARNING_PACKAGE: 'e-learning-package',
    FLASHCARD: 'flashcard',
};

/**
 * Completeness type mappings for activity types
 * Maps activity types to their completion tracking strategy
 */
export const COMPLETENESS_TYPES = {
    BOOLEAN: 'boolean',
    BOOLEAN_WITH_ATTEMPTS: 'boolean_with_attempts_remaining',
    PROGRESS: 'progress',
    IN_PROGRESS: 'in_progress',
};

/**
 * Activity type to completeness type mapping
 * Used to determine how completion is tracked for each activity type
 */
export const ACTIVITY_COMPLETENESS_MAP = {
    [ACTIVITY_TYPES.VIEW_INFORMATION]: COMPLETENESS_TYPES.BOOLEAN,
    [ACTIVITY_TYPES.ACKNOWLEDGE_INFORMATION]: COMPLETENESS_TYPES.BOOLEAN,
    [ACTIVITY_TYPES.FILE_SUBMISSION]: COMPLETENESS_TYPES.BOOLEAN_WITH_ATTEMPTS,
    [ACTIVITY_TYPES.DISCUSSION_THREAD]: COMPLETENESS_TYPES.PROGRESS,
    [ACTIVITY_TYPES.QUIZ]: COMPLETENESS_TYPES.IN_PROGRESS,
    [ACTIVITY_TYPES.PRESENTATION]: COMPLETENESS_TYPES.BOOLEAN,
    [ACTIVITY_TYPES.VIDEO]: COMPLETENESS_TYPES.BOOLEAN,
    [ACTIVITY_TYPES.SLIDE_DECK]: COMPLETENESS_TYPES.BOOLEAN,
    [ACTIVITY_TYPES.SURVEY]: COMPLETENESS_TYPES.BOOLEAN,
    [ACTIVITY_TYPES.LTI_RESOURCE_LINK]: COMPLETENESS_TYPES.BOOLEAN,
    [ACTIVITY_TYPES.LTI_TOOL]: COMPLETENESS_TYPES.BOOLEAN,
    [ACTIVITY_TYPES.EBOOK]: COMPLETENESS_TYPES.BOOLEAN,
    [ACTIVITY_TYPES.ELEARNING_PACKAGE]: COMPLETENESS_TYPES.BOOLEAN,
    [ACTIVITY_TYPES.FLASHCARD]: COMPLETENESS_TYPES.BOOLEAN,
    [ACTIVITY_TYPES.PODCAST]: COMPLETENESS_TYPES.PROGRESS,
};

/**
 * Helper function to check if an activity type is valid
 * @param {string} activityType - The activity type to validate
 * @returns {boolean} True if the activity type is valid
 */
export function isValidActivityType(activityType) {
    return Object.values(ACTIVITY_TYPES).includes(activityType);
}

/**
 * Helper function to get completeness type for an activity
 * @param {string} activityType - The activity type
 * @returns {string|null} The completeness type or null if not found
 */
export function getCompletenessType(activityType) {
    return ACTIVITY_COMPLETENESS_MAP[activityType] || null;
}
